import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Slide,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DialogCloseIcon from "@mui/icons-material/Close";
import ChevronDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  navTarget,
  scopeReady,
  searchNav,
} from "../../shared/navigationRegistry";
import useDebouncedValue from "../../shared/useDebouncedValue";
import { resolveViewedYearId } from "../../shared/schoolYear";
import { useFixedSidebar } from "../../contexts/FixedSidebarContext";
import { useBookmarks } from "../../contexts/BookmarksContext";
import BookmarksGrid from "./BookmarksGrid";
import SchoolNavMenu from "./SchoolNavMenu";
import SchoolNavMenuMobile from "./SchoolNavMenuMobile";
import DashboardNavControls from "./DashboardNavControls";
import DashboardSearchField from "./DashboardSearchField";
import DashboardSearchResults from "./DashboardSearchResults";
import DashboardShortcutHint from "./DashboardShortcutHint";
import { NavDrawerContext } from "../shared/NavDrawerButton";

// Mobile-only entrance: the launchpad slides up from the bottom edge like a
// sheet. Desktop keeps the default fade. Hoisted (and ref-forwarding, as MUI
// transitions require) so it is not rebuilt on every render.
const SlideUpTransition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));
SlideUpTransition.displayName = "SlideUpTransition";

export default function DashboardMenu({
  open,
  onClose,
  initialManage = false,
  appProps,
  schoolSlug,
  schoolYearId,
  navigationMode = "spa",
}) {
  const spaNavigate = useNavigate();
  const theme = useTheme();
  // Match the sidebar's desktop/mobile split so the nav section inside the
  // dialog renders the same format the user sees in the drawer/sidebar.
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const schoolId = appProps?.school?.id ?? null;
  const { enabled: sidebarLocked, setEnabled: setSidebarLocked } =
    useFixedSidebar();

  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 250);

  // Bookmarks live in a shared context (seeded once from appProps) so edits here
  // also re-render the Home screen's grid without a reload.
  const { bookmarkIds, setBookmarkIds, persistBookmarks } = useBookmarks();
  // Latest bookmarkIds, readable from the open-transition effect without making
  // that effect refire (and re-decide navHidden) on every add/remove.
  const bookmarkIdsRef = useRef(bookmarkIds);
  bookmarkIdsRef.current = bookmarkIds;
  const [manageMode, setManageMode] = useState(false);
  // True while a manage-mode edit (reorder/delete) is being persisted, so the
  // "Ok" button can show a spinner and block dismiss until the save settles.
  const [saving, setSaving] = useState(false);
  // Persist while tracking in-flight count so the spinner spans rapid edits.
  const savingCount = useRef(0);
  const saveBookmarks = (ids) => {
    savingCount.current += 1;
    setSaving(true);
    return persistBookmarks(ids).finally(() => {
      savingCount.current -= 1;
      if (savingCount.current === 0) setSaving(false);
    });
  };
  // Id of a just-added bookmark, briefly highlighted (and scrolled into view)
  // so the user sees the add land even when the grid is scrolled out of sight.
  const [highlightId, setHighlightId] = useState(null);
  // Nav hidden/shown: decided at open time from bookmark presence and never
  // auto-toggled while the user adds/removes bookmarks during the session.
  const [navHidden, setNavHidden] = useState(true);

  // Default year derivation, mirroring SchoolNavMenu - links resolve against
  // the staff's last-selected year, then the default school year (no selectors
  // in the dialog anymore).
  const yearId = resolveViewedYearId(appProps, schoolYearId);

  const ctx = {
    schoolId,
    schoolSlug,
    school: appProps?.school ?? null,
    permissions: appProps?.permissions ?? {},
    yearId,
  };

  // Reset transient UI state on the closed -> open transition and decide the
  // nav-hidden default from the CURRENT bookmarks (hidden once any bookmark
  // exists, shown while there are none). Decided only here so adding/removing
  // bookmarks mid-session never flips nav visibility.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setQuery("");
      // Start in manage mode when asked (the Home "Manage bookmarks" link), but
      // only if there is something to manage; otherwise fall back to normal.
      const startManage = initialManage && bookmarkIdsRef.current.length > 0;
      setManageMode(startManage);
      setNavHidden(bookmarkIdsRef.current.length > 0);
    }
    wasOpen.current = open;
  }, [open, initialManage]);

  const handleAddBookmark = (id) => {
    setBookmarkIds((prev) => {
      if (prev.includes(id)) return prev; // dedupe
      const next = [...prev, id]; // append to end
      persistBookmarks(next);
      return next;
    });
    setHighlightId(id);
  };

  // Ref into the bookmarks grid so we can hand the keyboard to its first tile
  // once the dialog finishes opening (see the transition onEntered below).
  const gridRef = useRef(null);
  // After the open transition settles (so MUI's focus trap has already run),
  // land focus on the first bookmark so arrow keys navigate the grid straight
  // away. Skipped on mobile (no keyboard), in manage mode (dnd owns the keys),
  // and when there are no bookmarks (the search field keeps its autoFocus).
  const focusFirstBookmark = () => {
    if (isMobile || manageMode || bookmarkIds.length === 0) return;
    gridRef.current?.focusFirst();
  };

  const handleManageStart = () => setManageMode(true);
  // Every edit in manage mode (reorder, delete) persists immediately, so there
  // is nothing to save or revert: "Ok" just leaves manage mode.
  const handleManageDone = () => setManageMode(false);

  const handleNavigate = (node) => {
    const target = navTarget(node, ctx);
    if (!target || !scopeReady(target, ctx)) return;
    const url = target.resolve(ctx);
    if (navigationMode === "browser") {
      window.location.assign(url);
    } else if (!target.v1) {
      spaNavigate(url);
    } else {
      window.location.href = url;
    }
    onClose();
  };

  const searchActive = debouncedQuery.trim().length > 0;
  const results = useMemo(
    () => (searchActive ? searchNav(debouncedQuery, ctx).slice(0, 30) : []),
    [searchActive, debouncedQuery, ctx]
  );

  // ---- Search-results keyboard nav (bookmarks grid owns its own) --------
  const searchRef = useRef(null);
  const resultRefs = useRef([]);
  const [focusedIndex, setFocusedIndex] = useState(0);

  useEffect(() => {
    setFocusedIndex(0);
  }, [results.length, searchActive]);

  const focusResult = (i) => {
    setFocusedIndex(i);
    resultRefs.current[i]?.focus();
  };

  const onSearchKeyDown = (e) => {
    if (e.key === "ArrowDown" && results.length > 0) {
      e.preventDefault();
      focusResult(0);
    } else if (e.key === "Enter" && results[focusedIndex]) {
      e.preventDefault();
      handleNavigate(results[focusedIndex].node);
    }
  };

  const onResultKeyDown = (e) => {
    if (e.key === "/") {
      e.preventDefault();
      searchRef.current?.focus();
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleNavigate(results[focusedIndex]?.node);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (focusedIndex + 1 < results.length) focusResult(focusedIndex + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (focusedIndex === 0) searchRef.current?.focus();
      else focusResult(focusedIndex - 1);
    }
  };

  // When a bookmark is just added, scroll the dialog back to the top so the
  // bookmarks grid (and the highlighted new tile) is in view, even if the user
  // was scrolled down in the menu when they clicked the + .
  const dialogContentRef = useRef(null);
  useEffect(() => {
    if (highlightId) {
      dialogContentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [highlightId]);

  // The reused nav menus read their close handler from NavDrawerContext; supply
  // one that closes this dialog (there is no real drawer here). Memoized so the
  // provider value is reference-stable across renders.
  const navDrawerValue = useMemo(() => ({ close: onClose }), [onClose]);

  const renderNavBody = () => {
    if (searchActive) {
      return (
        <DashboardSearchResults
          results={results}
          focusedIndex={focusedIndex}
          setItemRef={(i, el) => {
            resultRefs.current[i] = el;
          }}
          onFocus={(i) => setFocusedIndex(i)}
          onKeyDown={onResultKeyDown}
          onActivate={handleNavigate}
          onAddBookmark={handleAddBookmark}
          bookmarkedIds={bookmarkIds}
          ctx={ctx}
        />
      );
    }
    const NavMenu = isMobile ? SchoolNavMenuMobile : SchoolNavMenu;
    return (
      <NavMenu
        appProps={appProps}
        schoolSlug={schoolSlug}
        schoolYearId={schoolYearId}
        navigationMode={navigationMode}
        onAddBookmark={handleAddBookmark}
        bookmarkedIds={bookmarkIds}
        boxed
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      slots={isMobile ? { transition: SlideUpTransition } : undefined}
      slotProps={{
        paper: { sx: { position: "relative", overflow: "visible" } },
        transition: {
          onEntered: focusFirstBookmark,
          ...(isMobile && { timeout: { enter: 350, exit: 300 } }),
        },
      }}
    >
      {/* Desktop: a white floating close button straddling the dialog's
          top-right corner, half outside the paper (mobile uses the bottom
          actions bar below). Same style as the report card preview overlay's
          close button. */}
      {!isMobile && (
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{
            position: "absolute",
            top: -20,
            right: -20,
            zIndex: 1,
            background: "#FFFFFF",
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
            "&:hover": { background: "#FFFFFF" },
          }}
        >
          <DialogCloseIcon />
        </IconButton>
      )}
      <DialogContent
        ref={dialogContentRef}
        sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}
      >
        {/* Bookmarks grid (top) */}
        <BookmarksGrid
          ref={gridRef}
          bookmarkIds={bookmarkIds}
          ctx={ctx}
          isMobile={isMobile}
          manageMode={manageMode}
          highlightId={highlightId}
          onHighlightEnd={() => setHighlightId(null)}
          saving={saving}
          onReorder={(next) => {
            setBookmarkIds(next);
            saveBookmarks(next);
          }}
          onRemove={(id) =>
            setBookmarkIds((prev) => {
              const next = prev.filter((x) => x !== id);
              saveBookmarks(next);
              return next;
            })
          }
          onActivate={handleNavigate}
          onLongPress={handleManageStart}
          onManageStart={handleManageStart}
          onManageDone={handleManageDone}
        />

        {/* Navigation visibility controls + (when shown) search + nav menu */}
        {navHidden ? (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              startIcon={<ChevronDownIcon />}
              aria-label="Show navigation"
              onClick={() => setNavHidden(false)}
            >
              Show Navigation
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Hide / Lock controls: bare (no container), above the search. */}
            <DashboardNavControls
              isMobile={isMobile}
              hasBookmarks={bookmarkIds.length > 0}
              sidebarLocked={sidebarLocked}
              onHide={() => setNavHidden(true)}
              onLock={() => {
                setSidebarLocked(true);
                onClose();
              }}
            />

            {/* Search: its own glass container. */}
            <DashboardSearchField
              inputRef={searchRef}
              autoFocus={!isMobile}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onSearchKeyDown}
              onClear={() => {
                setQuery("");
                searchRef.current?.focus();
              }}
            />

            {/* Nav section: search results when typing, else the School Nav menu,
                each item in its own glass box. NavDrawerContext supplies the
                reused nav menus' close handler so an item click closes this
                dialog (no real drawer here). */}
            <NavDrawerContext.Provider value={navDrawerValue}>
              {renderNavBody()}
            </NavDrawerContext.Provider>
          </Box>
        )}
      </DialogContent>

      {/* Mobile: a fixed bottom actions bar replaces the old top-right X. It
          stays pinned while the content above it scrolls. */}
      {isMobile && (
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button variant="contained" color="primary" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      )}

      {/* Desktop: a discoverability hint for the keyboard shortcut that opens
          this launchpad from anywhere. Touch users have no keyboard, so it is
          desktop-only. */}
      {!isMobile && <DashboardShortcutHint />}
    </Dialog>
  );
}
